import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography,
  Table, TableBody, TableCell, TableRow,
  styled,
} from '@mui/material';
import irmHeader from 'assets/images/irm-header.png';
import PatientCodeBarcodes from 'components/PatientCodeBarcodes';

/* ── Indicator metadata ──────────────────────────────────────────────── */
// Every count feeding a rate is listed alongside that rate — previously
// this map only carried the *_rate keys, so every underlying count
// (follicles, retrieved, MII, PN1/2/3, embryo/compaction/blastocyst
// counts, per-method base counts) silently never made it onto the
// printed report even though it was saved and shown on the dashboard.
const DAY_INDICATORS = {
  0: [
    { key: 'follicles_above_14mm', label: 'No of Follicles Above 14mm', unit: '', section: 'RI' },
    { key: 'oocyte_retrieved', label: 'No of Oocyte Retrieved', unit: '', section: 'RI' },
    { key: 'oocyte_recovery_rate', label: 'Oocyte Recovery Rate', unit: '%', section: 'RI' },
    { key: 'mii_oocyte_count', label: 'MII Oocyte Count', unit: '', section: 'RI' },
    { key: 'mii_oocyte_proportion', label: 'MII Oocyte Proportion', unit: '%', section: 'RI' },
    { key: 'semen_user_type', label: 'Type of Semen User', unit: '', section: 'PI' },
    { key: 'semen_type', label: 'Type of Semen', unit: '', section: 'PI' },
    { key: 'semen_concentration', label: 'Semen Concentration', unit: ' M/ml', section: 'PI' },
    { key: 'semen_motility_rate', label: 'Semen Motility', unit: '%', section: 'PI' },
    { key: 'semen_morphology_rate', label: 'Semen Morphology', unit: '%', section: 'PI' },
    { key: 'semen_preparation_method', label: 'Method of Semen Preparation', unit: '', section: 'PI' },
    // unit is resolved at render time from the sibling 'post_wash_sperm_count_uom'
    // record (or omitted entirely when the value is the qualitative "FEW").
    { key: 'post_wash_no_tail_sperm_count', label: 'Post Wash Sperm Count', unit: '', section: 'PI' },
    { key: 'post_wash_motility_rate', label: 'Post Wash Motility Rate', unit: '%', section: 'PI' },
    { key: 'oocyte_inseminated_conventional', label: 'No of MII Oocyte Inseminated (Conventional)', unit: '', section: 'KPI' },
    { key: 'oocyte_injected', label: 'No of MII Oocyte Injected (ICSI)', unit: '', section: 'KPI' },
    { key: 'oocyte_damaged', label: 'No of Oocyte Damaged (ICSI)', unit: '', section: 'KPI' },
    { key: 'oocyte_damage_rate_icsi', label: 'Oocyte Damage Rate (ICSI)', unit: '%', section: 'KPI' },
    // Legacy (pre insemination-method split) — kept for older saved records
    { key: 'oocyte_damaged_conventional', label: 'No of Oocyte Damaged (Conventional)', unit: '', section: 'KPI' },
    { key: 'oocyte_damage_rate_conventional', label: 'Oocyte Damage Rate (Conventional)', unit: '%', section: 'KPI' },
  ],
  1: [
    { key: 'oocyte_inseminated', label: 'Base Count (Conventional)', unit: '', section: 'PI' },
    { key: 'pn0_oocyte_count_conventional', label: 'PN0 Oocyte Count (Conventional)', unit: '', section: 'PI' },
    { key: '0pn_rate_conventional', label: '0PN Rate (Conventional)', unit: '%', section: 'PI' },
    { key: 'pn1_oocyte_count_conventional', label: 'PN1 Oocyte Count (Conventional)', unit: '', section: 'PI' },
    { key: '1pn_rate_conventional', label: '1PN Rate (Conventional)', unit: '%', section: 'PI' },
    { key: 'pn2_oocyte_count_conventional', label: 'PN2 Oocyte Count (Conventional)', unit: '', section: 'PI' },
    { key: '2pn_rate_conventional', label: '2PN Rate (Conventional)', unit: '%', section: 'PI' },
    { key: 'pn3_oocyte_count_conventional', label: 'PN3 Oocyte Count (Conventional)', unit: '', section: 'PI' },
    { key: '3pn_rate_conventional', label: '3PN Rate (Conventional)', unit: '%', section: 'PI' },
    { key: 'oocyte_injected_day1', label: 'Base Count (ICSI)', unit: '', section: 'PI' },
    { key: 'pn0_oocyte_count_icsi', label: 'PN0 Oocyte Count (ICSI)', unit: '', section: 'PI' },
    { key: '0pn_rate_icsi', label: '0PN Rate (ICSI)', unit: '%', section: 'PI' },
    { key: 'pn1_oocyte_count_icsi', label: 'PN1 Oocyte Count (ICSI)', unit: '', section: 'PI' },
    { key: '1pn_rate_icsi', label: '1PN Rate (ICSI)', unit: '%', section: 'PI' },
    { key: 'pn2_oocyte_count_icsi', label: 'PN2 Oocyte Count (ICSI)', unit: '', section: 'PI' },
    { key: '2pn_rate_icsi', label: '2PN Rate (ICSI)', unit: '%', section: 'PI' },
    { key: 'pn3_oocyte_count_icsi', label: 'PN3 Oocyte Count (ICSI)', unit: '', section: 'PI' },
    { key: '3pn_rate_icsi', label: '3PN Rate (ICSI)', unit: '%', section: 'PI' },
    { key: 'total_oocyte_base_day1', label: 'Base Count (Total Combined)', unit: '', section: 'PI' },
    { key: 'total_pn0_oocyte_count', label: 'PN0 Oocyte Count (Total Combined)', unit: '', section: 'PI' },
    { key: 'total_0pn_rate', label: '0PN Rate (Total Combined)', unit: '%', section: 'PI' },
    { key: 'total_pn1_oocyte_count', label: 'PN1 Oocyte Count (Total Combined)', unit: '', section: 'PI' },
    { key: 'total_1pn_rate', label: '1PN Rate (Total Combined)', unit: '%', section: 'PI' },
    { key: 'total_pn2_oocyte_count', label: 'PN2 Oocyte Count (Total Combined)', unit: '', section: 'PI' },
    { key: 'total_2pn_rate', label: '2PN Rate (Total Combined)', unit: '%', section: 'PI' },
    { key: 'total_pn3_oocyte_count', label: 'PN3 Oocyte Count (Total Combined)', unit: '', section: 'PI' },
    { key: 'total_3pn_rate', label: '3PN Rate (Total Combined)', unit: '%', section: 'PI' },
    // Legacy (pre insemination-method split) — kept for older saved records
    { key: 'pn1_oocyte_count', label: 'PN1 Oocyte Count', unit: '', section: 'PI' },
    { key: '1pn_rate', label: '1PN Rate', unit: '%', section: 'PI' },
    { key: 'pn2_oocyte_count', label: 'PN2 Oocyte Count', unit: '', section: 'PI' },
    { key: '2pn_rate', label: '2PN Rate', unit: '%', section: 'PI' },
    { key: 'pn3_oocyte_count', label: 'PN3 Oocyte Count', unit: '', section: 'PI' },
    { key: '3pn_rate', label: '3PN Rate', unit: '%', section: 'PI' },
  ],
  3: [
    { key: 'embryo_count_conventional', label: 'No of Embryos (Conventional)', unit: '', section: 'KPI' },
    { key: 'embryo_formation_rate_conventional', label: 'Embryo Formation Rate (Conventional)', unit: '%', section: 'KPI' },
    { key: 'good_quality_embryo_count_conventional', label: 'No of Good Quality Embryo (Conventional)', unit: '', section: 'KPI' },
    { key: 'good_quality_embryo_rate_conventional', label: 'Good Quality Embryo Rate (Conventional)', unit: '%', section: 'KPI' },
    { key: 'embryo_count_icsi', label: 'No of Embryos (ICSI)', unit: '', section: 'KPI' },
    { key: 'embryo_formation_rate_icsi', label: 'Embryo Formation Rate (ICSI)', unit: '%', section: 'KPI' },
    { key: 'good_quality_embryo_count_icsi', label: 'No of Good Quality Embryo (ICSI)', unit: '', section: 'KPI' },
    { key: 'good_quality_embryo_rate_icsi', label: 'Good Quality Embryo Rate (ICSI)', unit: '%', section: 'KPI' },
    { key: 'total_embryo_count', label: 'No of Embryos (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_embryo_formation_rate', label: 'Embryo Formation Rate (Total Combined)', unit: '%', section: 'KPI' },
    { key: 'total_good_quality_embryo_count', label: 'No of Good Quality Embryo (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_good_quality_embryo_rate', label: 'Good Quality Embryo Rate (Total Combined)', unit: '%', section: 'KPI' },
    // Legacy (pre insemination-method split) — kept for older saved records
    { key: 'embryo_count', label: 'No of Embryos', unit: '', section: 'KPI' },
    { key: 'embryo_formation_rate', label: 'Embryo Formation Rate', unit: '%', section: 'KPI' },
  ],
  4: [
    { key: 'd4_day3_embryo_count_conventional', label: 'No of DAY-3 Embryos (Conventional)', unit: '', section: 'KPI' },
    { key: 'compacted_embryo_count_conventional', label: 'No of Compacted Embryos (Conventional)', unit: '', section: 'KPI' },
    { key: 'compaction_rate_conventional', label: 'Compaction Rate (Conventional)', unit: '%', section: 'KPI' },
    { key: 'd4_day3_embryo_count_icsi', label: 'No of DAY-3 Embryos (ICSI)', unit: '', section: 'KPI' },
    { key: 'compacted_embryo_count_icsi', label: 'No of Compacted Embryos (ICSI)', unit: '', section: 'KPI' },
    { key: 'compaction_rate_icsi', label: 'Compaction Rate (ICSI)', unit: '%', section: 'KPI' },
    { key: 'total_d4_day3_embryo_count', label: 'No of DAY-3 Embryos (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_compacted_embryo_count', label: 'No of Compacted Embryos (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_compaction_rate', label: 'Compaction Rate (Total Combined)', unit: '%', section: 'KPI' },
    // Legacy (pre insemination-method split) — kept for older saved records
    { key: 'd4_day3_embryo_count', label: 'No of DAY-3 Embryos', unit: '', section: 'KPI' },
    { key: 'compacted_embryo_count', label: 'No of Compacted Embryos', unit: '', section: 'KPI' },
    { key: 'compaction_rate', label: 'Compaction Rate', unit: '%', section: 'KPI' },
  ],
  5: [
    { key: 'd5_day3_formation_count_conventional', label: 'No of DAY-3 Embryos (Conventional)', unit: '', section: 'KPI' },
    { key: 'blastocyst_count_conventional', label: 'No of Blastocysts (Conventional)', unit: '', section: 'KPI' },
    { key: 'blastocyst_formation_rate_conventional', label: 'Blastocyst Formation Rate (Conventional)', unit: '%', section: 'KPI' },
    { key: 'usable_blastocyst_count_conventional', label: 'No of Usable Blastocysts (Conventional)', unit: '', section: 'KPI' },
    { key: 'usable_blastocyst_formation_rate_conventional', label: 'Usable Blastocyst Formation Rate (Conventional)', unit: '%', section: 'KPI' },
    { key: 'd5_day3_formation_count_icsi', label: 'No of DAY-3 Embryos (ICSI)', unit: '', section: 'KPI' },
    { key: 'blastocyst_count_icsi', label: 'No of Blastocysts (ICSI)', unit: '', section: 'KPI' },
    { key: 'blastocyst_formation_rate_icsi', label: 'Blastocyst Formation Rate (ICSI)', unit: '%', section: 'KPI' },
    { key: 'usable_blastocyst_count_icsi', label: 'No of Usable Blastocysts (ICSI)', unit: '', section: 'KPI' },
    { key: 'usable_blastocyst_formation_rate_icsi', label: 'Usable Blastocyst Formation Rate (ICSI)', unit: '%', section: 'KPI' },
    { key: 'total_d5_day3_formation_count', label: 'No of DAY-3 Embryos (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_blastocyst_count', label: 'No of Blastocysts (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_blastocyst_formation_rate', label: 'Blastocyst Formation Rate (Total Combined)', unit: '%', section: 'KPI' },
    { key: 'total_usable_blastocyst_count', label: 'No of Usable Blastocysts (Total Combined)', unit: '', section: 'KPI' },
    { key: 'total_usable_blastocyst_formation_rate', label: 'Usable Blastocyst Formation Rate (Total Combined)', unit: '%', section: 'KPI' },
    // Legacy (pre insemination-method split) — kept for older saved records
    { key: 'd5_day3_formation_count', label: 'No of DAY-3 Embryos', unit: '', section: 'KPI' },
    { key: 'blastocyst_count', label: 'No of Blastocysts', unit: '', section: 'KPI' },
    { key: 'blastocyst_formation_rate', label: 'Blastocyst Formation Rate', unit: '%', section: 'KPI' },
    { key: 'usable_blastocyst_count', label: 'No of Usable Blastocysts', unit: '', section: 'KPI' },
    { key: 'usable_blastocyst_formation_rate', label: 'Usable Blastocyst Formation Rate', unit: '%', section: 'KPI' },
  ],
};

const CIK_STORAGE_MEDIA = 'cryo_storage_media';
const CIK_STORAGE_LN_LEVEL = 'cryo_storage_ln_level';
// Repeatable Vitrification entry — target = JSON{ dayNo, noPerStraw,
// reserved, rescued }, indicator_value = type. Replaces the old fixed
// DAY-0 form; legacy DAY-0/3/4/5 keys below still print for old records.
const CIK_VITRI_ENTRY = 'cryo_vitri_entry';
const CIK_VITRI_D0_OOCYTE = 'cryo_vitri_d0_oocyte';
// D3/D4/D5 manual Cryo Preserved/Rescued/Refroze entries — the form that
// writes these was disabled 2026-07-23 (fully duplicated by per-straw
// mark-used tracking below), but older saved data still lives under these
// keys and must keep printing.
const CIK_VITRI_D3_EMBRYO_CRYO = 'cryo_vitri_d3_embryo_cryo';
const CIK_VITRI_D3_RESCUED = 'cryo_vitri_d3_rescued';
const CIK_VITRI_D3_REFROZE = 'cryo_vitri_d3_refroze';
const CIK_VITRI_D3_RESCUED_RATE = 'cryo_vitri_d3_rescued_rate';
const CIK_VITRI_D4_EMBRYO_CRYO = 'cryo_vitri_d4_embryo_cryo';
const CIK_VITRI_D4_RESCUED = 'cryo_vitri_d4_rescued';
const CIK_VITRI_D4_REFROZE = 'cryo_vitri_d4_refroze';
const CIK_VITRI_D4_RESCUED_RATE = 'cryo_vitri_d4_rescued_rate';
const CIK_VITRI_D5_EMBRYO_CRYO = 'cryo_vitri_d5_embryo_cryo';
const CIK_VITRI_D5_RESCUED = 'cryo_vitri_d5_rescued';
const CIK_VITRI_D5_REFROZE = 'cryo_vitri_d5_refroze';
const CIK_VITRI_D5_RESCUED_RATE = 'cryo_vitri_d5_rescued_rate';
const CIK_VITRI_D5_CAN_ENTRY = 'cryo_vitri_d5_can_entry';
const CIK_VITRI_D5_CANE_ENTRY = 'cryo_vitri_d5_cane_entry';
const CIK_VITRI_D5_STRAW_ENTRY = 'cryo_vitri_d5_straw_entry';
const CIK_WARMING_OOCYTE = 'cryo_warming_oocyte_pct';
const CIK_WARMING_EMBRYO = 'cryo_warming_embryo_pct';
const CIK_WARMING_BLASTO = 'cryo_warming_blasto_pct';
const CIK_SURVIVAL_OOCYTE = 'cryo_survival_oocyte_pct';
const CIK_SURVIVAL_EMBRYO = 'cryo_survival_embryo_pct';
const CIK_SURVIVAL_BLASTO = 'cryo_survival_blasto_pct';
const CIK_DATE_CRYOPRESERVATION = 'cryo_date_cryopreservation';
const CIK_DATE_THAWING = 'cryo_date_thawing';

const SPIK_ENTRY = 'semen_pres_entry';
const SPIK_VIAL = 'semen_pres_vial';

const BIK_TUBING_RATE = 'biopsy_tubing_rate';
const BIK_PGT_TYPE = 'biopsy_pgt_type';
const BIK_EUPLOIDY_RATE = 'biopsy_euploidy_rate';

const TIK_EMBRYO_COUNT = 'transfer_embryo_count';
const TIK_EMBRYO_DAYS = 'transfer_embryo_days';
const TIK_EMBRYO_TYPE = 'transfer_embryo_type';
const TIK_EMBRYO_GRADE = 'transfer_embryo_grade';
const TIK_TRANSFER_PROCESS = 'transfer_process';
const TIK_TRANSFER_TECHNIQUE = 'transfer_technique';

const IIK_EMBRYOS_TRANSFERRED = 'impl_embryos_transferred';
const IIK_EMBRYOS_IMPLANTED = 'impl_embryos_implanted';
const IIK_IMPLEMENTATION = 'impl_implementation';
const IIK_REMARKS = 'impl_remarks';

const AIK_COUNT = 'andrology_count';
const AIK_MOTILITY = 'andrology_motility';
const AIK_PROGRESSIVE_MOTILITY = 'andrology_progressive_motility';
const AIK_MORPHOLOGY = 'andrology_morphology';
const AIK_DFI = 'andrology_dna_fragmentation';
const AIK_DFI_BEFORE = 'andrology_dna_before_treatment';
const AIK_DFI_AFTER = 'andrology_dna_after_treatment';
const AIK_REMARKS = 'andrology_remarks';

const getDFILabel = (val) => {
  const s = String(val ?? '').trim();
  if (!s) return '';
  const n = parseFloat(s);
  if (isNaN(n)) return '';
  if (n <= 21) return 'Normal';
  return 'High';
};

/* ── Styled components ───────────────────────────────────────────────── */
const PrintPage = styled(Box)({
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  fontSize: '11px',
  color: '#000',
  backgroundColor: '#fff',
  width: '210mm',
  padding: '10mm 12mm 14mm',
  boxSizing: 'border-box',
  '@media print': {
    width: '100%',
    padding: '8mm 10mm 12mm',
  },
});

const SecHeader = styled(Box)({
  borderBottom: '2px solid #222',
  marginBottom: '6px',
  marginTop: '16px',
  paddingBottom: '3px',
  pageBreakAfter: 'avoid',
  breakAfter: 'avoid',
});

const PTable = styled(Table)({
  borderCollapse: 'collapse',
  width: '100%',
  '& .MuiTableCell-body': {
    fontSize: '10px',
    padding: '4px 7px',
    border: '1px solid #bbb',
    color: '#000',
    verticalAlign: 'top',
    lineHeight: 1.4,
  },
  '& .MuiTableRow-root': {
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  },
});

const TH = (extra = {}) => ({
  backgroundColor: '#e8e8e8',
  fontWeight: 'bold',
  fontSize: '10px',
  padding: '5px 7px',
  border: '1px solid #888',
  color: '#000',
  lineHeight: 1.3,
  ...extra,
});

/* Inline key-value row — label cell is shaded */
function KVRow({ label, value, note }) {
  return (
    <TableRow>
      <TableCell sx={{ width: '34%', backgroundColor: '#f2f2f2', fontWeight: '600 !important', fontSize: '10px !important', border: '1px solid #bbb !important', padding: '4px 7px !important', color: '#000 !important' }}>
        {label}
      </TableCell>
      <TableCell sx={{ fontSize: '10px !important', border: '1px solid #bbb !important', padding: '4px 7px !important', color: '#000 !important' }}>
        {value || '—'}
      </TableCell>
      {note !== undefined && (
        <TableCell sx={{ width: '24%', fontSize: '10px !important', border: '1px solid #bbb !important', padding: '4px 7px !important', color: '#333 !important', fontStyle: 'italic' }}>
          {note || ''}
        </TableCell>
      )}
    </TableRow>
  );
}
KVRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  note: PropTypes.string,
};
KVRow.defaultProps = { value: '', note: undefined };

/* Semen Preservation demographics row (Patient/Spouse/Donor) — ABORh (blood
   group) gets bold red "NOT AVAILABLE" styling when missing instead of a
   plain dash, same intent as the dashboard/modal's highlighted badge.
   Labeled "ABORh" (not "Blood Group") to match the source investigation
   param's own name. */
function PersonRow({ label, person }) {
  const hasBg = !!person?.bloodGroup;
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: '600 !important' }}>{label} — {person?.name || '—'}</TableCell>
      <TableCell>{person?.code || '—'}</TableCell>
      <TableCell sx={hasBg ? { fontWeight: '700 !important' } : { fontWeight: '700 !important', color: '#c62828 !important', backgroundColor: '#ffebee !important' }}>
        {hasBg ? person.bloodGroup : 'NOT AVAILABLE'}
      </TableCell>
      <TableCell>{person?.age ? `${person.age} yrs` : '—'}</TableCell>
      <TableCell>{person?.dob || '—'}</TableCell>
    </TableRow>
  );
}
PersonRow.propTypes = {
  label: PropTypes.string.isRequired,
  person: PropTypes.shape({ name: PropTypes.string, code: PropTypes.string, dob: PropTypes.string, age: PropTypes.number, bloodGroup: PropTypes.string }),
};
PersonRow.defaultProps = { person: null };

/* ═══════════════════════════════════════════════════════════════════════ */

const IVFLabPrint = React.forwardRef(function IVFLabPrint(props, ref) {
  const {
    patientName, spouseName, patientCode, coupleCode, spouseCode, procedureCode, procedureDate,
    procedureCategory, procedureSubCategory, procedureStatus, mobile,
    allRecords, patientPerson, spousePerson, donorPerson, semenUserType,
  } = props;

  const recs = Array.isArray(allRecords) ? allRecords : [];
  const getR = (key, day) => recs.find(
    (r) => Number(r?.day_number) === day && String(r?.indicator_key || '').toLowerCase() === key,
  );
  const getRec = (key) => recs.find((r) => r?.indicator_key === key);

  const labConditionRecords = recs.filter((r) => r?.indicator_key === 'lab_condition_deviation');
  const cryoRecs = recs.filter((r) => String(r?.indicator_key || '').startsWith('cryo_'));
  const biopsyRecs = recs.filter((r) => String(r?.indicator_key || '').startsWith('biopsy_'));
  const transferRecs = recs.filter((r) => String(r?.indicator_key || '').startsWith('transfer_'));
  const implRecs = recs.filter((r) => String(r?.indicator_key || '').startsWith('impl_'));
  const outcomeRecords = recs.filter((r) => r?.indicator_key === 'outcome_record');
  const andrologyRecs = recs.filter((r) => String(r?.indicator_key || '').startsWith('andrology_'));

  const parseOutcome = (rec) => { try { return JSON.parse(rec?.indicator_value || '{}'); } catch { return {}; } };
  const parseT = (rec) => { try { return JSON.parse(rec?.target || '{}'); } catch { return {}; } };
  const vitriEntryRecs = recs.filter((r) => r?.indicator_key === CIK_VITRI_ENTRY);
  const canEntryRecs = recs.filter((r) => r?.indicator_key === CIK_VITRI_D5_CAN_ENTRY);
  const caneEntryRecs = recs.filter((r) => r?.indicator_key === CIK_VITRI_D5_CANE_ENTRY);
  const strawEntryRecs = recs.filter((r) => r?.indicator_key === CIK_VITRI_D5_STRAW_ENTRY);
  // Flatten CAN → CANE → Straw into one row per straw (or a shorter row
  // when a CAN/CANE has no children yet, so nothing is silently dropped).
  const canHierarchyRows = (() => {
    const rows = [];
    canEntryRecs.forEach((can) => {
      const canes = caneEntryRecs.filter((c) => parseT(c).canId === can.id);
      if (canes.length === 0) {
        rows.push({ canType: can.target, canisterNo: can.indicator_value, canRemarks: can.remarks });
        return;
      }
      canes.forEach((cane) => {
        const caneT = parseT(cane);
        const straws = strawEntryRecs.filter((s) => parseT(s).caneId === cane.id);
        if (straws.length === 0) {
          rows.push({ canType: can.target, canisterNo: can.indicator_value, caneNo: cane.indicator_value, tag: caneT.tag, goblet: caneT.goblet, caneRemarks: cane.remarks });
          return;
        }
        straws.forEach((straw) => {
          const strawT = parseT(straw);
          rows.push({
            canType: can.target, canisterNo: can.indicator_value,
            caneNo: cane.indicator_value, tag: caneT.tag, goblet: caneT.goblet,
            strawNo: straw.indicator_value, preservedAt: strawT.preservedAt, used: strawT.used, usedAt: strawT.usedAt,
            rescuedDay: strawT.rescuedDay, rescuedCount: strawT.rescuedCount, strawRemarks: straw.remarks,
          });
        });
      });
    });
    return rows;
  })();

  // Semen Preservation — Entry → exactly two Vials, flattened one row per
  // vial (mirrors canHierarchyRows above).
  const semenEntryRecs = recs.filter((r) => r?.indicator_key === SPIK_ENTRY);
  const semenVialRecs = recs.filter((r) => r?.indicator_key === SPIK_VIAL);
  const semenHierarchyRows = (() => {
    const rows = [];
    semenEntryRecs.forEach((entry) => {
      const et = parseT(entry);
      const vials = semenVialRecs.filter((vRec) => parseT(vRec).entryId === entry.id)
        .sort((a, b) => Number(a.indicator_value) - Number(b.indicator_value));
      if (vials.length === 0) {
        rows.push({ mediaUsed: entry.indicator_value, canNo: et.canNo, canisterNo: et.canisterNo, preservedAt: et.preservedAt, beforeCount: et.beforeCount, beforeMotility: et.beforeMotility, entryRemarks: entry.remarks });
        return;
      }
      vials.forEach((vial) => {
        const vt = parseT(vial);
        rows.push({
          mediaUsed: entry.indicator_value, canNo: et.canNo, canisterNo: et.canisterNo, preservedAt: et.preservedAt,
          beforeCount: et.beforeCount, beforeMotility: et.beforeMotility,
          vialNo: vial.indicator_value, used: vt.used, usedAt: vt.usedAt, prepMethod: vt.prepMethod, postWashCount: vt.postWashCount, afterMotility: vt.afterMotility,
          vialRemarks: vial.remarks,
        });
      });
    });
    return rows;
  })();

  // Straw Rescue Rate — pooled per DAY across every used straw, same
  // sum-then-rate approach (not an average of individual straws' rates)
  // as the dashboard's strawRescueStatsByDay.
  const strawRescueStatsByDay = (day) => {
    const straws = strawEntryRecs.filter((r) => {
      const t = parseT(r);
      return t.used && Number(t.rescuedDay) === day;
    });
    const totalEmbryos = straws.reduce((sum, r) => sum + (Number(parseT(r).embryoCount) || 0), 0);
    const totalRescued = straws.reduce((sum, r) => sum + (Number(parseT(r).rescuedCount) || 0), 0);
    const rate = totalEmbryos > 0 ? Math.round((totalRescued / totalEmbryos) * 100) : 0;
    return { strawCount: straws.length, totalEmbryos, totalRescued, rate };
  };
  const strawRescueRows = [0, 3, 4, 5].map((day) => ({ day, ...strawRescueStatsByDay(day) })).filter((row) => row.strawCount > 0);

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  // Media values are saved as 'Others:<free text>' when the "Others" option
  // is picked — decode that for display, same as CIK_STORAGE_MEDIA below.
  const fmtMedia = (v) => (v && String(v).startsWith('Others:') ? `Others: ${String(v).slice(7).trim()}` : v);
  const fmtDateTime = (d) => d
    ? new Date(String(d).replace(' ', 'T')).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
    : '—';

  const hasDayData = (day) => DAY_INDICATORS[day]?.some((ind) => {
    const v = getR(ind.key, day)?.indicator_value;
    return v !== null && v !== undefined && String(v).trim() !== '';
  });

  const hasAnyDayData = [0, 1, 3, 4, 5].some(hasDayData);
  const hasCryo = cryoRecs.length > 0 || semenEntryRecs.length > 0;
  const hasBiopsy = biopsyRecs.length > 0;
  const hasTransfer = transferRecs.length > 0;
  const hasImpl = implRecs.length > 0;
  const hasOutcome = outcomeRecords.length > 0;
  const hasAndrology = andrologyRecs.length > 0;

  const v = (key) => getRec(key)?.indicator_value || '';
  const r = (key) => getRec(key)?.remarks || '';

  return (
    /* Off-screen wrapper — keeps the component out of visible layout */
    <Box
      sx={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {/* ref is on the printable content itself, not the positioning wrapper */}
      <PrintPage ref={ref}>

        {/* ── Header image ──────────────────────────────────────────── */}
        <Box sx={{ mb: '8px' }}>
          <img src={irmHeader} alt="IRM Header" style={{ width: '100%', display: 'block' }} />
        </Box>

        {/* ── Report title ──────────────────────────────────────────── */}
        <Box sx={{ textAlign: 'center', borderTop: '2.5px solid #000', borderBottom: '2.5px solid #000', py: '6px', mb: '12px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 'bold', letterSpacing: '1px', textTransform: 'uppercase', color: '#000' }}>
            IVF Lab Performance Report
          </Typography>
        </Box>

        {/* ── Patient Information ───────────────────────────────────── */}
        <PTable size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: '20%', backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Procedure ID</TableCell>
              <TableCell sx={{ width: '30%', border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{procedureCode}</TableCell>
              <TableCell sx={{ width: '20%', backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Patient Name</TableCell>
              <TableCell sx={{ width: '30%', border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{patientName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Initiated Date</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{procedureDate}</TableCell>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Spouse Name</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{spouseName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Patient Code</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{patientCode}</TableCell>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Mobile</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{mobile}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Category</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{procedureCategory} / {procedureSubCategory}</TableCell>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Status</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px', fontWeight: '600' }}>{procedureStatus?.replace(/_/g, ' ')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Couple Code</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{coupleCode}</TableCell>
              <TableCell sx={{ backgroundColor: '#e8e8e8', fontWeight: 'bold', border: '1px solid #888', padding: '4px 7px', color: '#000', fontSize: '10px' }}>Spouse Code</TableCell>
              <TableCell sx={{ border: '1px solid #bbb', padding: '4px 7px', color: '#000', fontSize: '10px' }}>{spouseCode}</TableCell>
            </TableRow>
          </TableBody>
        </PTable>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: '8px' }}>
          <PatientCodeBarcodes mono layout="row" patientCode={patientCode !== 'N/A' ? patientCode : undefined} coupleCode={coupleCode !== 'N/A' ? coupleCode : undefined} spouseCode={spouseCode !== 'N/A' ? spouseCode : undefined} documentCode={procedureCode !== 'N/A' ? procedureCode : undefined} documentLabel="Procedure No" />
        </Box>

        {/* ── 1. Lab Performance ────────────────────────────────────── */}
        {hasAnyDayData && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                1. Lab Performance (Day-wise)
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '10%' })}>Day</TableCell>
                  <TableCell sx={TH({ width: '10%' })}>Section</TableCell>
                  <TableCell sx={TH({ width: '38%' })}>Indicator</TableCell>
                  <TableCell sx={TH({ width: '16%' })}>Value</TableCell>
                  <TableCell sx={TH()}>Remarks</TableCell>
                </TableRow>
                {[0, 1, 3, 4, 5].flatMap((day) =>
                  DAY_INDICATORS[day]
                    .map((ind) => {
                      const rec = getR(ind.key, day);
                      if (!rec?.indicator_value) return null;
                      const val = String(rec.indicator_value);
                      // Post Wash Motile Sperm Count carries a selectable UOM
                      // (stored separately) and can be the qualitative "FEW".
                      let unit = ind.unit;
                      if (ind.key === 'post_wash_no_tail_sperm_count') {
                        unit = /^\d/.test(val)
                          ? ` ${getR('post_wash_sperm_count_uom', day)?.indicator_value || 'million/ml'}`
                          : '';
                      }
                      return (
                        <TableRow key={`${day}-${ind.key}`}>
                          <TableCell>DAY {day}</TableCell>
                          <TableCell>{ind.section}</TableCell>
                          <TableCell>{ind.label}</TableCell>
                          <TableCell sx={{ fontWeight: '600 !important' }}>{val}{unit}</TableCell>
                          <TableCell>{rec.remarks || ''}</TableCell>
                        </TableRow>
                      );
                    })
                    .filter(Boolean),
                )}
              </TableBody>
            </PTable>

          </Box>
        )}

        {/* ── 2. Lab Condition ─────────────────────────────────────────── */}
        {labConditionRecords.length > 0 && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                2. Lab Condition
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '6%' })}>#</TableCell>
                  <TableCell sx={TH({ width: '16%' })}>Type</TableCell>
                  <TableCell sx={TH({ width: '30%' })}>Details</TableCell>
                  <TableCell sx={TH()}>Remarks</TableCell>
                  <TableCell sx={TH({ width: '16%' })}>Date</TableCell>
                </TableRow>
                {labConditionRecords.map((rec, i) => {
                  const lt = parseT(rec);
                  const type = rec.indicator_value || '—';
                  const typeFields = type === 'INCUBATOR'
                    ? [lt.co2 ? `CO2: ${lt.co2}` : null, lt.temperature ? `Temp: ${lt.temperature}°C` : null, lt.o2 ? `O2: ${lt.o2}` : null]
                    : type === 'LAB ENVIRONMENT'
                      ? [lt.voc ? `VOC: ${lt.voc}` : null, lt.humidity ? `Humidity: ${lt.humidity}` : null, lt.temperature ? `Temp: ${lt.temperature}°C` : null]
                      : [];
                  const details = [...typeFields, lt.recordTime ? `Recorded: ${fmtDateTime(lt.recordTime)}` : null].filter(Boolean).join(', ') || '—';
                  return (
                    <TableRow key={`lc-${i}`}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{type}</TableCell>
                      <TableCell>{details}</TableCell>
                      <TableCell>{rec.remarks || '—'}</TableCell>
                      <TableCell>{fmtDate(rec.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </PTable>
          </Box>
        )}

        {/* ── 3. Cryopreservation ───────────────────────────────────── */}
        {hasCryo && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                3. Cryopreservation
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '34%' })}>Parameter</TableCell>
                  <TableCell sx={TH()}>Value</TableCell>
                  <TableCell sx={TH({ width: '24%' })}>Remarks</TableCell>
                </TableRow>
                {/* Cryopreservation / Thawing Dates */}
                {v(CIK_DATE_CRYOPRESERVATION) && <KVRow label="Cryopreservation Date" value={fmtDateTime(v(CIK_DATE_CRYOPRESERVATION))} note={r(CIK_DATE_CRYOPRESERVATION)} />}
                {v(CIK_DATE_THAWING) && <KVRow label="Thawing Date" value={fmtDateTime(v(CIK_DATE_THAWING))} note={r(CIK_DATE_THAWING)} />}
                {/* Storage */}
                {v(CIK_STORAGE_MEDIA) && (
                  <KVRow
                    label="Storage Media"
                    value={v(CIK_STORAGE_MEDIA).startsWith('Others:') ? `Others: ${v(CIK_STORAGE_MEDIA).slice(7).trim()}` : v(CIK_STORAGE_MEDIA)}
                    note={r(CIK_STORAGE_MEDIA)}
                  />
                )}
                {v(CIK_STORAGE_LN_LEVEL) && <KVRow label="Storage LN Level" value={v(CIK_STORAGE_LN_LEVEL)} note={r(CIK_STORAGE_LN_LEVEL)} />}
                {/* Vitrification D0 */}
                {v(CIK_VITRI_D0_OOCYTE) && <KVRow label="Vitrification D0 — Oocyte" value={v(CIK_VITRI_D0_OOCYTE)} note={r(CIK_VITRI_D0_OOCYTE)} />}
                {/* Vitrification D3/D4/D5 — manual entry, superseded by
                    per-straw tracking below for new records but older
                    saved data still lives under these keys. */}
                {v(CIK_VITRI_D3_EMBRYO_CRYO) && <KVRow label="No of Embryos Cryo Preserved (D3)" value={v(CIK_VITRI_D3_EMBRYO_CRYO)} />}
                {v(CIK_VITRI_D3_RESCUED) && <KVRow label="No of Embryos Rescued After Thawing (D3)" value={v(CIK_VITRI_D3_RESCUED)} />}
                {v(CIK_VITRI_D3_REFROZE) && <KVRow label="No of Refroze (D3)" value={v(CIK_VITRI_D3_REFROZE)} />}
                {v(CIK_VITRI_D3_RESCUED_RATE) && <KVRow label="Embryo Rescued Proportion Rate (D3)" value={`${v(CIK_VITRI_D3_RESCUED_RATE)}%`} note={r(CIK_VITRI_D3_RESCUED_RATE)} />}
                {v(CIK_VITRI_D4_EMBRYO_CRYO) && <KVRow label="No of Embryos Cryo Preserved (D4)" value={v(CIK_VITRI_D4_EMBRYO_CRYO)} />}
                {v(CIK_VITRI_D4_RESCUED) && <KVRow label="No of Embryos Rescued After Thawing (D4)" value={v(CIK_VITRI_D4_RESCUED)} />}
                {v(CIK_VITRI_D4_REFROZE) && <KVRow label="No of Refroze (D4)" value={v(CIK_VITRI_D4_REFROZE)} />}
                {v(CIK_VITRI_D4_RESCUED_RATE) && <KVRow label="Embryo Rescued Proportion Rate (D4)" value={`${v(CIK_VITRI_D4_RESCUED_RATE)}%`} note={r(CIK_VITRI_D4_RESCUED_RATE)} />}
                {v(CIK_VITRI_D5_EMBRYO_CRYO) && <KVRow label="No of Embryos Cryo Preserved (D5)" value={v(CIK_VITRI_D5_EMBRYO_CRYO)} />}
                {v(CIK_VITRI_D5_RESCUED) && <KVRow label="No of Embryos Rescued After Thawing (D5)" value={v(CIK_VITRI_D5_RESCUED)} />}
                {v(CIK_VITRI_D5_REFROZE) && <KVRow label="No of Refroze (D5)" value={v(CIK_VITRI_D5_REFROZE)} />}
                {v(CIK_VITRI_D5_RESCUED_RATE) && <KVRow label="Embryo Rescued Proportion Rate (D5)" value={`${v(CIK_VITRI_D5_RESCUED_RATE)}%`} note={r(CIK_VITRI_D5_RESCUED_RATE)} />}
                {/* Warming */}
                {v(CIK_WARMING_OOCYTE) && <KVRow label="Warming Rate — Oocyte" value={`${v(CIK_WARMING_OOCYTE)}%`} note={r(CIK_WARMING_OOCYTE)} />}
                {v(CIK_WARMING_EMBRYO) && <KVRow label="Warming Rate — Embryo" value={`${v(CIK_WARMING_EMBRYO)}%`} note={r(CIK_WARMING_EMBRYO)} />}
                {v(CIK_WARMING_BLASTO) && <KVRow label="Warming Rate — Blastocyst" value={`${v(CIK_WARMING_BLASTO)}%`} note={r(CIK_WARMING_BLASTO)} />}
                {/* Survival */}
                {v(CIK_SURVIVAL_OOCYTE) && <KVRow label="Survival Rate — Oocyte" value={`${v(CIK_SURVIVAL_OOCYTE)}%`} note={r(CIK_SURVIVAL_OOCYTE)} />}
                {v(CIK_SURVIVAL_EMBRYO) && <KVRow label="Survival Rate — Embryo" value={`${v(CIK_SURVIVAL_EMBRYO)}%`} note={r(CIK_SURVIVAL_EMBRYO)} />}
                {v(CIK_SURVIVAL_BLASTO) && <KVRow label="Survival Rate — Blastocyst" value={`${v(CIK_SURVIVAL_BLASTO)}%`} note={r(CIK_SURVIVAL_BLASTO)} />}
              </TableBody>
            </PTable>

            {/* Vitrification entries sub-table (repeatable) */}
            {vitriEntryRecs.length > 0 && (
              <Box sx={{ mt: '8px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000', mb: '4px', ml: '2px' }}>
                  Vitrification Entries
                </Typography>
                <PTable size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={TH({ width: '16%' })}>Type</TableCell>
                      <TableCell sx={TH({ width: '10%' })}>Day No</TableCell>
                      <TableCell sx={TH({ width: '12%' })}>No / Straw</TableCell>
                      <TableCell sx={TH({ width: '14%' })}>Reserved</TableCell>
                      <TableCell sx={TH({ width: '18%' })}>Rescued (Thawing)</TableCell>
                      <TableCell sx={TH({ width: '10%' })}>%</TableCell>
                      <TableCell sx={TH()}>Remarks</TableCell>
                    </TableRow>
                    {vitriEntryRecs.map((rec, i) => {
                      const t = parseT(rec);
                      const reserved = Number(t.reserved);
                      const rescued = Number(t.rescued);
                      const pct = reserved > 0 ? Math.round((rescued / reserved) * 100) : null;
                      return (
                        <TableRow key={`vitri-e-${rec.id ?? i}`}>
                          <TableCell sx={{ fontWeight: '600 !important' }}>{rec.indicator_value || '—'}</TableCell>
                          <TableCell>{t.dayNo || '—'}</TableCell>
                          <TableCell>{t.noPerStraw || '—'}</TableCell>
                          <TableCell>{t.reserved || '—'}</TableCell>
                          <TableCell>{t.rescued || '—'}</TableCell>
                          <TableCell sx={{ fontWeight: '600 !important' }}>{pct !== null ? `${pct}%` : '—'}</TableCell>
                          <TableCell>{rec.remarks || ''}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </PTable>
              </Box>
            )}

            {/* CAN → Canister → CANE → Straw sub-table */}
            {canHierarchyRows.length > 0 && (
              <Box sx={{ mt: '8px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000', mb: '4px', ml: '2px' }}>
                  Vitrification D5 — CAN / Canister / CANE / Straw Details
                </Typography>
                <PTable size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={TH({ width: '8%' })}>CAN</TableCell>
                      <TableCell sx={TH({ width: '8%' })}>Canister</TableCell>
                      <TableCell sx={TH({ width: '8%' })}>CANE</TableCell>
                      <TableCell sx={TH({ width: '8%' })}>Tag</TableCell>
                      <TableCell sx={TH({ width: '8%' })}>Goblet</TableCell>
                      <TableCell sx={TH({ width: '8%' })}>Straw</TableCell>
                      <TableCell sx={TH({ width: '14%' })}>Preserved</TableCell>
                      <TableCell sx={TH({ width: '16%' })}>Status</TableCell>
                      <TableCell sx={TH()}>Remarks</TableCell>
                    </TableRow>
                    {canHierarchyRows.map((row, i) => (
                      <TableRow key={`can-h-${i}`}>
                        <TableCell>{row.canType || '—'}</TableCell>
                        <TableCell>{row.canisterNo || '—'}</TableCell>
                        <TableCell>{row.caneNo || '—'}</TableCell>
                        <TableCell>{row.tag || '—'}</TableCell>
                        <TableCell>{row.goblet || '—'}</TableCell>
                        <TableCell>{row.strawNo || '—'}</TableCell>
                        <TableCell>{row.strawNo && row.preservedAt ? fmtDateTime(row.preservedAt) : '—'}</TableCell>
                        <TableCell>
                          {row.strawNo
                            ? (row.used
                              ? `Used${row.usedAt ? ` (${fmtDateTime(row.usedAt)})` : ''}${row.rescuedDay !== undefined && row.rescuedDay !== null && row.rescuedDay !== '' ? ` · DAY ${row.rescuedDay}` : ''}${row.rescuedCount ? ` · ${row.rescuedCount} rescued` : ''}`
                              : 'Available')
                            : '—'}
                        </TableCell>
                        <TableCell>{row.strawRemarks || row.caneRemarks || row.canRemarks || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </PTable>
              </Box>
            )}

            {/* Straw Rescue Rate — pooled per DAY across every used straw */}
            {strawRescueRows.length > 0 && (
              <Box sx={{ mt: '8px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000', mb: '4px', ml: '2px' }}>
                  Straw Rescue Rate
                </Typography>
                <PTable size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={TH({ width: '14%' })}>Day</TableCell>
                      <TableCell sx={TH({ width: '20%' })}>Straws Used</TableCell>
                      <TableCell sx={TH({ width: '20%' })}>Embryos Rescued</TableCell>
                      <TableCell sx={TH()}>Rescue Rate</TableCell>
                    </TableRow>
                    {strawRescueRows.map((row) => (
                      <TableRow key={`srr-${row.day}`}>
                        <TableCell sx={{ fontWeight: '600 !important' }}>DAY {row.day}</TableCell>
                        <TableCell>{row.strawCount}</TableCell>
                        <TableCell>{row.totalRescued} / {row.totalEmbryos}</TableCell>
                        <TableCell sx={{ fontWeight: '600 !important' }}>{row.rate}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </PTable>
              </Box>
            )}

            {/* Semen Preservation */}
            {semenEntryRecs.length > 0 && (
              <Box sx={{ mt: '8px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#000', mb: '4px', ml: '2px' }}>
                  Semen Preservation
                </Typography>
                <PTable size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={TH({ width: '18%' })}>Person</TableCell>
                      <TableCell sx={TH({ width: '20%' })}>Code</TableCell>
                      <TableCell sx={TH({ width: '12%' })}>ABORh</TableCell>
                      <TableCell sx={TH({ width: '10%' })}>Age</TableCell>
                      <TableCell sx={TH()}>Date of Birth</TableCell>
                    </TableRow>
                    <PersonRow label="Patient" person={patientPerson} />
                    <PersonRow label="Spouse" person={spousePerson} />
                    {semenUserType === 'Donor' && <PersonRow label="Donor" person={donorPerson} />}
                  </TableBody>
                </PTable>
                <PTable size="small" sx={{ mt: '6px' }}>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={TH({ width: '12%' })}>Media Used</TableCell>
                      <TableCell sx={TH({ width: '7%' })}>Can No</TableCell>
                      <TableCell sx={TH({ width: '8%' })}>Canister</TableCell>
                      <TableCell sx={TH({ width: '6%' })}>Vial</TableCell>
                      <TableCell sx={TH({ width: '12%' })}>Preserved</TableCell>
                      <TableCell sx={TH({ width: '13%' })}>Before Cryo</TableCell>
                      <TableCell sx={TH({ width: '18%' })}>Status / After Thawing</TableCell>
                      <TableCell sx={TH({ width: '10%' })}>Prep Method</TableCell>
                      <TableCell sx={TH()}>Remarks</TableCell>
                    </TableRow>
                    {semenHierarchyRows.map((row, i) => (
                      <TableRow key={`sp-h-${i}`}>
                        <TableCell>{fmtMedia(row.mediaUsed) || '—'}</TableCell>
                        <TableCell>{row.canNo || '—'}</TableCell>
                        <TableCell>{row.canisterNo || '—'}</TableCell>
                        <TableCell>{row.vialNo || '—'}</TableCell>
                        <TableCell>{row.vialNo && row.preservedAt ? fmtDateTime(row.preservedAt) : '—'}</TableCell>
                        <TableCell>
                          {(row.beforeCount || row.beforeMotility)
                            ? `${row.beforeCount ? `${row.beforeCount} M/ml` : ''}${row.beforeCount && row.beforeMotility ? ' · ' : ''}${row.beforeMotility ? `${row.beforeMotility}%` : ''}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.vialNo
                            ? (row.used
                              ? `Used${row.usedAt ? ` (${fmtDateTime(row.usedAt)})` : ''}${row.postWashCount ? ` · ${row.postWashCount} M/ml` : ''}${row.afterMotility ? ` · ${row.afterMotility}% motility` : ''}`
                              : 'Available')
                            : '—'}
                        </TableCell>
                        <TableCell>{row.prepMethod || '—'}</TableCell>
                        <TableCell>{row.vialRemarks || row.entryRemarks || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </PTable>
              </Box>
            )}
          </Box>
        )}

        {/* ── 4. Embryo Biopsy ──────────────────────────────────────── */}
        {hasBiopsy && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                4. Embryo Biopsy
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '34%' })}>Parameter</TableCell>
                  <TableCell sx={TH()}>Value</TableCell>
                  <TableCell sx={TH({ width: '24%' })}>Remarks</TableCell>
                </TableRow>
                {v(BIK_TUBING_RATE) && <KVRow label="Tubing Rate" value={`${v(BIK_TUBING_RATE)}%`} note={r(BIK_TUBING_RATE)} />}
                {v(BIK_PGT_TYPE) && <KVRow label="PGT Type" value={v(BIK_PGT_TYPE)} />}
                {v(BIK_EUPLOIDY_RATE) && <KVRow label="Euploidy Rate" value={`${v(BIK_EUPLOIDY_RATE)}%`} note={r(BIK_EUPLOIDY_RATE)} />}
              </TableBody>
            </PTable>
          </Box>
        )}

        {/* ── 5. Embryo Transfer ────────────────────────────────────── */}
        {hasTransfer && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                5. Embryo Transfer
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '34%' })}>Parameter</TableCell>
                  <TableCell sx={TH()}>Value</TableCell>
                  <TableCell sx={TH({ width: '24%' })}>Remarks</TableCell>
                </TableRow>
                {v(TIK_EMBRYO_COUNT) && <KVRow label="Embryo Count" value={v(TIK_EMBRYO_COUNT)} note={r(TIK_EMBRYO_COUNT)} />}
                {v(TIK_EMBRYO_DAYS) && <KVRow label="Embryo Days" value={v(TIK_EMBRYO_DAYS)} note={r(TIK_EMBRYO_DAYS)} />}
                {v(TIK_EMBRYO_TYPE) && <KVRow label="Embryo Type" value={v(TIK_EMBRYO_TYPE)} note={r(TIK_EMBRYO_TYPE)} />}
                {v(TIK_EMBRYO_GRADE) && <KVRow label="Embryo Grade" value={v(TIK_EMBRYO_GRADE)} note={r(TIK_EMBRYO_GRADE)} />}
                {v(TIK_TRANSFER_PROCESS) && <KVRow label="Transfer Process" value={v(TIK_TRANSFER_PROCESS)} />}
                {v(TIK_TRANSFER_TECHNIQUE) && <KVRow label="Transfer Technique" value={v(TIK_TRANSFER_TECHNIQUE)} />}
              </TableBody>
            </PTable>
          </Box>
        )}

        {/* ── 6. Implementation ─────────────────────────────────────── */}
        {hasImpl && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                6. Implantation
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '34%' })}>Parameter</TableCell>
                  <TableCell sx={TH()}>Value</TableCell>
                  <TableCell sx={TH({ width: '24%' })}>Remarks</TableCell>
                </TableRow>
                {v(IIK_EMBRYOS_TRANSFERRED) && <KVRow label="Embryos Transferred" value={v(IIK_EMBRYOS_TRANSFERRED)} note={r(IIK_EMBRYOS_TRANSFERRED)} />}
                {v(IIK_EMBRYOS_IMPLANTED) && <KVRow label="Embryos Implanted" value={v(IIK_EMBRYOS_IMPLANTED)} note={r(IIK_EMBRYOS_IMPLANTED)} />}
                {v(IIK_IMPLEMENTATION) && <KVRow label="Outcome" value={v(IIK_IMPLEMENTATION)} />}
                {v(IIK_REMARKS) && <KVRow label="Remarks" value={v(IIK_REMARKS)} />}
              </TableBody>
            </PTable>
          </Box>
        )}

        {/* ── 7. Outcome Records ────────────────────────────────────── */}
        {hasOutcome && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                7. Outcome
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '6%' })}>#</TableCell>
                  <TableCell sx={TH({ width: '20%' })}>Pregnancy Type</TableCell>
                  <TableCell sx={TH({ width: '22%' })}>Sub Type</TableCell>
                  <TableCell sx={TH({ width: '14%' })}>Weeks</TableCell>
                  <TableCell sx={TH()}>Remarks</TableCell>
                  <TableCell sx={TH({ width: '18%' })}>Date</TableCell>
                </TableRow>
                {outcomeRecords.map((rec, i) => {
                  const p = parseOutcome(rec);
                  return (
                    <TableRow key={`oc-${i}`}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{p.pregnancyType || '—'}</TableCell>
                      <TableCell>{p.subType || '—'}</TableCell>
                      <TableCell>{p.miscarriageWeeks ? `${p.miscarriageWeeks} wks` : '—'}</TableCell>
                      <TableCell>{rec.remarks || '—'}</TableCell>
                      <TableCell>{fmtDate(rec.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </PTable>
          </Box>
        )}

        {/* ── 8. Andrology ──────────────────────────────────────────── */}
        {hasAndrology && (
          <Box>
            <SecHeader>
              <Typography sx={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#000' }}>
                8. Andrology — Semen Analysis
              </Typography>
            </SecHeader>
            <PTable size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={TH({ width: '34%' })}>Parameter</TableCell>
                  <TableCell sx={TH()}>Value</TableCell>
                  <TableCell sx={TH({ width: '24%' })}>Notes</TableCell>
                </TableRow>
                {v(AIK_COUNT) && <KVRow label="Count" value={`${v(AIK_COUNT)} M/ml`} note={r(AIK_COUNT)} />}
                {v(AIK_MOTILITY) && <KVRow label="Motility" value={`${v(AIK_MOTILITY)}%`} note={r(AIK_MOTILITY)} />}
                {v(AIK_PROGRESSIVE_MOTILITY) && <KVRow label="Progressive Motility" value={`${v(AIK_PROGRESSIVE_MOTILITY)}%`} note={r(AIK_PROGRESSIVE_MOTILITY)} />}
                {v(AIK_MORPHOLOGY) && <KVRow label="Morphology" value={`${v(AIK_MORPHOLOGY)}%`} note={r(AIK_MORPHOLOGY)} />}
                {v(AIK_DFI) && (
                  <KVRow
                    label="SP DNA Fragmentation (DFI)"
                    value={`${v(AIK_DFI)}%`}
                    note={getDFILabel(v(AIK_DFI))}
                  />
                )}
                {v(AIK_DFI_BEFORE) && <KVRow label="DFI Before Treatment" value={`${v(AIK_DFI_BEFORE)}%`} />}
                {v(AIK_DFI_AFTER) && <KVRow label="DFI After Treatment" value={`${v(AIK_DFI_AFTER)}%`} />}
                {v(AIK_REMARKS) && <KVRow label="Remarks" value={v(AIK_REMARKS)} />}
              </TableBody>
            </PTable>
          </Box>
        )}

        {/* ── Footer ────────────────────────────────────────────────── */}
        <Box sx={{ mt: '24px', borderTop: '1.5px solid #555', pt: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Typography sx={{ fontSize: '9px', color: '#444' }}>
            Procedure: {procedureCode} &nbsp;·&nbsp; Patient: {patientCode}
          </Typography>
          <Typography sx={{ fontSize: '9px', color: '#444' }}>
            Printed on {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </Typography>
        </Box>

      </PrintPage>
    </Box>
  );
});

IVFLabPrint.displayName = 'IVFLabPrint';

IVFLabPrint.propTypes = {
  patientName: PropTypes.string,
  spouseName: PropTypes.string,
  patientCode: PropTypes.string,
  coupleCode: PropTypes.string,
  spouseCode: PropTypes.string,
  procedureCode: PropTypes.string,
  procedureDate: PropTypes.string,
  procedureCategory: PropTypes.string,
  procedureSubCategory: PropTypes.string,
  procedureStatus: PropTypes.string,
  mobile: PropTypes.string,
  allRecords: PropTypes.array,
  patientPerson: PropTypes.shape({ name: PropTypes.string, code: PropTypes.string, dob: PropTypes.string, age: PropTypes.number, bloodGroup: PropTypes.string }),
  spousePerson: PropTypes.shape({ name: PropTypes.string, code: PropTypes.string, dob: PropTypes.string, age: PropTypes.number, bloodGroup: PropTypes.string }),
  donorPerson: PropTypes.shape({ name: PropTypes.string, code: PropTypes.string, dob: PropTypes.string, age: PropTypes.number, bloodGroup: PropTypes.string }),
  semenUserType: PropTypes.string,
};

IVFLabPrint.defaultProps = {
  patientName: 'N/A', spouseName: 'N/A', patientCode: 'N/A',
  coupleCode: 'N/A', spouseCode: 'N/A',
  procedureCode: 'N/A', procedureDate: 'N/A', procedureCategory: 'IVF',
  procedureSubCategory: 'SELF', procedureStatus: 'N/A', mobile: 'N/A',
  allRecords: [],
  patientPerson: null,
  spousePerson: null,
  donorPerson: null,
  semenUserType: '',
};

export default IVFLabPrint;
